import { useEffect, useState, type FormEvent } from "react";
import { useI18n, type StringKey } from "../i18n";
import { addEmployee, loadContractTypes, loadOrganisation } from "../data";
import type { Branch, ContractType, Department, Workspace } from "../types";

const today = () => new Date().toISOString().slice(0, 10);

export function AddEmployee({ ws, onDone }: { ws: Workspace; onDone: (employmentId: string | null) => void }) {
  const { t, pick, lang } = useI18n();
  const companyId = ws.membership.company_id;
  const [f, setF] = useState<Record<string, string>>({ hire_date: today(), nationality: "EG", contract_type: "permanent", fte: "1" });
  const [departments, setDepartments] = useState<Department[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [types, setTypes] = useState<ContractType[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void Promise.all([loadOrganisation(companyId), loadContractTypes()]).then(([o, ct]) => {
      setDepartments(o.departments);
      setBranches(o.branches);
      setTypes(ct);
    });
  }, [companyId]);

  const set = (k: string) => (e: { target: { value: string } }) => setF((prev) => ({ ...prev, [k]: e.target.value }));
  const needsEnd = types.find((c) => c.code === f.contract_type)?.needs_end_date ?? false;
  const egyptian = (f.nationality || "EG").toUpperCase() === "EG";

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const id = await addEmployee(companyId, { ...f, nationality: (f.nationality || "EG").toUpperCase() });
      onDone(id);
    } catch (err) {
      setError(err instanceof Error ? err.message : t("errorGeneric"));
      setBusy(false);
    }
  }

  const field = (k: string, label: StringKey, opts: { type?: string; dir?: "ltr" | "rtl"; required?: boolean; hint?: StringKey } = {}) => (
    <label className="field">
      <span>{t(label)}{opts.required ? " *" : ""}</span>
      <input id={`emp-${k}`} type={opts.type ?? "text"} dir={opts.dir} value={f[k] ?? ""} onChange={set(k)} required={opts.required} />
      {opts.hint && <small>{t(opts.hint)}</small>}
    </label>
  );

  return (
    <div className="page">
      <button type="button" className="link back" onClick={() => onDone(null)}>← {t("backToList")}</button>
      <form className="card form-card" onSubmit={submit}>
        <h2>{t("addEmployee")}</h2>

        <fieldset>
          <legend>{t("sectionPerson")}</legend>
          <div className="field-row">
            {field("first_name", "firstName", { dir: "ltr", required: true })}
            {field("last_name", "lastName", { dir: "ltr", required: true })}
          </div>
          <div className="field-row">
            {field("first_name_ar", "firstNameAr", { dir: "rtl" })}
            {field("last_name_ar", "lastNameAr", { dir: "rtl" })}
          </div>
          <div className="field-row">
            <label className="field">
              <span>{t("gender")}</span>
              <select id="emp-gender" value={f.gender ?? ""} onChange={set("gender")}>
                <option value="">—</option>
                <option value="female">{t("female")}</option>
                <option value="male">{t("male")}</option>
              </select>
            </label>
            {field("date_of_birth", "dateOfBirth", { type: "date" })}
            <label className="field">
              <span>{t("nationality")}</span>
              <select id="emp-nat-kind" value={egyptian ? "EG" : "OTHER"}
                onChange={(e) => setF((p) => ({ ...p, nationality: e.target.value === "EG" ? "EG" : "" }))}>
                <option value="EG">{t("egyptian")}</option>
                <option value="OTHER">{t("otherNationality")}</option>
              </select>
            </label>
            {!egyptian && (
              <label className="field">
                <span>ISO</span>
                <input id="emp-nationality" dir="ltr" maxLength={2} value={f.nationality ?? ""} onChange={set("nationality")} />
              </label>
            )}
          </div>
          <div className="field-row">
            {egyptian && field("national_id", "nationalId", { dir: "ltr", hint: "nationalIdHint" })}
            {field("mobile", "mobile", { type: "tel", dir: "ltr" })}
            {field("personal_email", "personalEmail", { type: "email", dir: "ltr" })}
          </div>
        </fieldset>

        <fieldset>
          <legend>{t("sectionJob")}</legend>
          <div className="field-row">
            {field("job_title", "jobTitle", { dir: "ltr", required: true })}
            {field("job_title_ar", "jobTitleAr", { dir: "rtl" })}
          </div>
          <div className="field-row">
            <label className="field">
              <span>{t("department")}</span>
              <select id="emp-dept" value={f.department_id ?? ""} onChange={set("department_id")}>
                <option value="">—</option>
                {departments.map((d) => <option key={d.id} value={d.id}>{pick(d.name, d.name_ar)}</option>)}
              </select>
            </label>
            <label className="field">
              <span>{t("branch")}</span>
              <select id="emp-branch" value={f.branch_id ?? ""} onChange={set("branch_id")}>
                <option value="">{lang === "ar" ? "المقر الرئيسي" : "Head office"}</option>
                {branches.filter((b) => !b.is_head_office).map((b) => <option key={b.id} value={b.id}>{pick(b.name, b.name_ar)}</option>)}
              </select>
            </label>
            {field("grade", "grade")}
          </div>
          <div className="field-row">
            {field("hire_date", "hireDate", { type: "date", required: true })}
            {field("employee_number", "employeeNumber", { dir: "ltr", hint: "autoNumber" })}
            {field("work_email", "workEmail", { type: "email", dir: "ltr" })}
          </div>
        </fieldset>

        <fieldset>
          <legend>{t("sectionContract")}</legend>
          <div className="field-row">
            <label className="field">
              <span>{t("contractType")}</span>
              <select id="emp-contract" value={f.contract_type} onChange={set("contract_type")}>
                {types.map((c) => <option key={c.code} value={c.code}>{pick(c.name_en, c.name_ar)}</option>)}
              </select>
            </label>
            {needsEnd && field("contract_end_date", "contractEnd", { type: "date", required: true })}
            {field("probation_end_date", "probationEnd", { type: "date", hint: "probationHint" })}
            {field("fte", "fte", { type: "number" })}
          </div>
        </fieldset>

        {error && <p className="alert danger" role="alert">{error}</p>}
        <div className="actions">
          <button type="button" className="btn secondary" onClick={() => onDone(null)} disabled={busy}>{t("cancel")}</button>
          <button className="btn primary" disabled={busy || !f.first_name || !f.last_name || !f.job_title || !f.hire_date}>{t("save")}</button>
        </div>
      </form>
    </div>
  );
}
